import { useEffect } from 'react';

import { INACCURACY } from 'constants/params';
import { LogLevel } from 'services/logger.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { useLocation, useAppNavigator, Path } from 'components/_hooks/use-router';
import { getSbPosition, parseClipId } from 'components/app/_helpers/_app.helper';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useSelectorCb } from 'components/app/selector/_mediator/use-selector-cb';
import { useSbContext } from 'components/app/storyboard/_mediator/sb.provider';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { appLogger } from 'components/app/_mediator/app-logger.slice';
import { EMPTY_CELL, setBackupListener, setSelectedMarkers } from './editor.slice';
import { setBackupModalListener, setRecentlyMovedClips } from './editor-controls.slice';

const ERR_ID = 'use-editor.ts';

export const useEditor = () => {
  const { path } = useLocation();
  const { navigateTo } = useAppNavigator();
  const { srSelectClip, srSelectFile } = useSelectorCb();
  const { wsGetCursorTime } = useAudioTrackContext();
  const { sbListenClip } = useSbContext();
  const { sbSeekTo } = useSbCb();
  const { index, backups } = useAppSelector((state) => state.backup);
  const backupListener = useAppSelector((state) => state.editor.backupListener);
  const storyboard = useAppSelector((state) => state.editor.storyBoard);
  const duration = useAppSelector((state) => state.audio.duration);
  const markers = useAppSelector((state) => state.editor.markers);
  const dispatch = useAppDispatch();

  /* clip selection */

  useEffect(() => {
    if (!backupListener) return;
    const backup = backups[index];

    // Log abnormal case: backupListener is set but backup doesn't exist
    if (!backup) {
      const isIndexOutOfBounds = index >= backups.length;
      const isEmptyBackups = backups.length === 0;

      dispatch(
        appLogger({
          level: LogLevel.Error,
          error: {
            name: `[${ERR_ID}]: backupListener set but backup not found`,
            message: JSON.stringify({
              backupListener: backupListener.action,
              index,
              backupsLength: backups.length,
              isIndexOutOfBounds,
              isEmptyBackups,
            }),
          },
        })
      );
      dispatch(setBackupListener(null));
      return;
    }

    const params = backup.params;
    const action = backupListener.action;

    if (!params) {
      dispatch(setBackupListener(null));
      return;
    }

    if (params.activeClip) {
      const clipId = params.activeClip[action === 'undo' ? 0 : 1];
      switch (path) {
        case Path.selector: {
          if (!clipId) break;
          const { mediaFileId } = parseClipId(clipId);
          srSelectFile(mediaFileId);
          srSelectClip(clipId);
          break;
        }
        case Path.storyboard: {
          if (clipId === EMPTY_CELL) {
            sbSeekTo(wsGetCursorTime());
            break;
          }
          if (
            (action === 'undo' && params.action === 'addClip') ||
            (action === 'redo' && params.action === 'delClip')
          ) {
            sbSeekTo(wsGetCursorTime());
            break;
          }
          if (params.action === 'resizeRight') {
            if (!markers || !duration) {
              dispatch(setBackupListener(null));
              return;
            }
            const cell = getSbPosition(clipId, storyboard).cell;
            sbSeekTo((markers[cell] ?? duration) - INACCURACY * 2);
          }
          const { track, cell } = getSbPosition(clipId, storyboard);
          sbListenClip.current = { clipId, cell, track };
          break;
        }
      }
    }
    const isUndoRedo = action === 'undo' || action === 'redo';

    if (params.selectedClips) {
      if (isUndoRedo && path !== Path.storyboard) {
        dispatch(setBackupModalListener(true));
        navigateTo(Path.storyboard);
      }
      const selectedClips = params.selectedClips[action === 'undo' ? 0 : 1];
      dispatch(setRecentlyMovedClips([]));
      dispatch(setRecentlyMovedClips(selectedClips));
    }
    if (params.selectedMarkers) {
      if (isUndoRedo && path !== Path.storyboard) {
        dispatch(setBackupModalListener(true));
        navigateTo(Path.storyboard);
      }
      const selectedMarkers = params.selectedMarkers[action === 'undo' ? 0 : 1];
      dispatch(setSelectedMarkers(selectedMarkers));
    }

    dispatch(setBackupListener(null));
  }, [backupListener]);
};
